/* @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import URI from 'jsuri';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';
import path from 'js/lib/path';
import user from 'js/lib/user';
import { isOutsourcing } from 'js/utils/emailOutsourcingUtils';

// @ts-expect-error ts-migrate(7034) FIXME: Variable 'rootPath' implicitly has type 'any' in s... Remove this comment to see the full error message
import { rootPath } from 'pages/open-course/common/constants';
import groupAuthorizationPromise from 'pages/open-course/common/promises/groupAuthorizationPromise';

import type { Theme } from '@coursera/cds-core';
import { ActionOverflow, withTheme } from '@coursera/cds-core';

import {
  hideAdminDetails,
  highlightPost,
  showAdminDetails,
  showReplyEditor,
  unhighlightPost,
} from 'bundles/discussions/actions/DropdownActions';
import Delete from 'bundles/discussions/components/Delete';
import EditWithModerationModal from 'bundles/discussions/components/EditWithModerationModal';
import FlaggingModal from 'bundles/discussions/components/FlaggingModal';
import HighlightModal from 'bundles/discussions/components/HighlightModal';
import ShareModal from 'bundles/discussions/components/ShareModal';
import discussionsForumsHOC from 'bundles/discussions/components/discussionsForumsHOC';
import EditThreadModal from 'bundles/discussions/components/forumsV2-ForumThreads/__components/ModerationDropdown/EditThreadModal';
import type { Contributor } from 'bundles/discussions/lib/types';
import { buildUrl, getLearnerDetailedViewLink } from 'bundles/discussions/utils/discussionsUrl';
import routerConnectToCurrentForum from 'bundles/discussions/utils/routerConnectToCurrentForum';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type GroupForumsV1 from 'bundles/naptimejs/resources/groupForums.v1';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandCourseForumsV1 from 'bundles/naptimejs/resources/onDemandCourseForums.v1';

/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import type OnDemandMentorForumsV1 from 'bundles/naptimejs/resources/onDemandMentorForums.v1';
import ProgrammingPermissionsUtils from 'bundles/programming-common/utils/ProgrammingPermissionsUtils';

import _t from 'i18n!nls/discussions';

import type { ForumPostWithCreator } from '../../__providers__/ForumPostDataProvider/__types__';

type PropsFromCaller = {
  creator: Contributor;
  post: ForumPostWithCreator;
  onDeleteSuccess: () => {};
  isHighlighted: boolean;
  forumLink: string;
  isGradedDiscussionPrompt: boolean;
  courseSlug: string;
  courseId: string;
  userId: number;
  hasModerationRole: boolean;
  currentForum: OnDemandCourseForumsV1 | OnDemandMentorForumsV1 | GroupForumsV1;
  ariaDescribedBy: string;
  theme: Theme;
  showingAdminDetails: boolean;
};

type PropsFromStore = {
  hasTeachingRole: boolean;
  isProgrammingItem: boolean;
  itemId?: string;
  isSuperuser: boolean;
  authenticated: boolean;
  question: ForumPostWithCreator;
};

type PropsToComponent = PropsFromCaller & PropsFromStore;

type State = {
  showDeleteModal: boolean;
  canModerate: boolean;
  showEditThreadModal: boolean;
  showEditWithModerationModal: boolean;
  showShareModal: boolean;
  showHighlightModal: boolean;
  showFlaggingModal: boolean;
};

class ModerationDropdown extends React.Component<PropsToComponent, State> {
  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof ModerationDropdown.contextTypes>;

  static defaultProps = {
    isGradedDiscussionPrompt: false,
  };

  _isMounted = false;

  // We'll store a reference to our container DOM node so we can restore focus.
  node: HTMLDivElement | null = null;

  constructor(props: $TSFixMe, context: $TSFixMe) {
    super(props, context);

    this.state = {
      showDeleteModal: false,
      canModerate: false,
      showEditThreadModal: false,
      showEditWithModerationModal: false,
      showShareModal: false,
      showHighlightModal: false,
      showFlaggingModal: false,
    };
  }

  componentWillMount() {
    const { authenticated, isSuperuser, currentForum, hasModerationRole } = this.props;

    const groupId = currentForum && currentForum.forumType.definition.groupId;
    if (groupId) {
      groupAuthorizationPromise(groupId, authenticated, isSuperuser).then((result: $TSFixMe) => {
        if (this._isMounted) {
          this.setState(result);
        }
      });
    } else {
      this.setState({ canModerate: hasModerationRole || isOutsourcing(user) });
    }
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  isModalClosing(prevState: State) {
    return (
      (!this.state.showEditThreadModal && prevState.showEditThreadModal) ||
      (!this.state.showDeleteModal && prevState.showDeleteModal) ||
      (!this.state.showEditWithModerationModal && prevState.showEditWithModerationModal) ||
      (!this.state.showShareModal && prevState.showShareModal) ||
      (!this.state.showHighlightModal && prevState.showHighlightModal) ||
      (!this.state.showFlaggingModal && prevState.showFlaggingModal)
    );
  }

  componentDidUpdate(_prevProps: $TSFixMe, prevState: State) {
    if (this.isModalClosing(prevState)) {
      const moreButton = this.node?.querySelector('button');
      if (moreButton) {
        (moreButton as HTMLButtonElement).focus();
      } else {
        this.node?.focus();
      }
    }
  }

  onEditClick = () => {
    const { creator, userId, post } = this.props;
    const postedByUser = creator?.userId === userId;

    if (!postedByUser && post.type !== 'question') {
      this.showEditWithModerationModal();
    } else {
      this.showEdit();
    }
  };

  onFlaggingClick = () => {
    this.setState({ showFlaggingModal: true });
  };

  onShareClick = () => {
    this.setState({ showShareModal: true });
  };

  showAdminDetails = () => {
    const { executeAction } = this.context;
    const { post } = this.props;
    executeAction(showAdminDetails, { post });
  };

  hideAdminDetails = () => {
    const { executeAction } = this.context;
    const { post } = this.props;
    executeAction(hideAdminDetails, { post });
  };

  showUserGrades = () => {
    const { post } = this.props;
    window.open(getLearnerDetailedViewLink(post.creatorId), '_blank');
  };

  showDeleteModal = () => {
    this.setState({ showDeleteModal: true });
  };

  editWithModerationModalSuccess = (reason: $TSFixMe, dontNotify: $TSFixMe) => {
    this.setState({ showEditWithModerationModal: false });
    this.showEditReply(reason, dontNotify);
  };

  showEdit = () => {
    const { post } = this.props;
    if (post.type === 'question') {
      this.showEditQuestion();
    } else {
      this.showEditReply();
    }
  };

  // Edit Question
  showEditQuestion() {
    this.setState({ showEditThreadModal: true });
  }

  // Edit Answer & Comment
  showEditReply(reason?: $TSFixMe, dontNotify?: $TSFixMe) {
    const { executeAction } = this.context;
    const { post } = this.props;
    executeAction(showReplyEditor, {
      reply: post,
      reason,
      dontNotify,
    });
  }

  // Edit Moderator Modal
  showEditWithModerationModal() {
    this.setState({ showEditWithModerationModal: true });
  }

  showSubmissions = () => {
    const { post, courseSlug, itemId, courseId } = this.props;
    const submissionUrl = new URI(
      `/teach/${courseSlug}/grading/assignment-grading/${itemId}/submission/${post.creatorId}`
    )
      // Note: always use base branch as versionId. use courseBranchId when PARTNER-11638 is resolved
      .addQueryParam('versionId', courseId)
      .toString();

    window.open(submissionUrl, '_blank');
  };

  render() {
    const {
      post,
      userId,
      forumLink,
      courseId,
      creator,
      hasTeachingRole,
      isSuperuser,
      isProgrammingItem,
      isHighlighted,
      onDeleteSuccess,
      isGradedDiscussionPrompt,
      ariaDescribedBy,
      showingAdminDetails,
      courseSlug,
    } = this.props;

    const {
      showDeleteModal,
      showEditThreadModal,
      showEditWithModerationModal,
      showFlaggingModal,
      showShareModal,
      showHighlightModal,
      canModerate,
    } = this.state;

    const postedByUser = creator?.userId === userId;
    const hasProgrammingAdminPermissions = ProgrammingPermissionsUtils.hasAdminPrivileges(hasTeachingRole);
    const ariaLabel = creator?.fullName
      ? _t(`More Settings Menu for #{userName}'s post`, { userName: creator?.fullName })
      : _t('More Settings Menu');

    const forumAllThreadsSearchLinkCheck = forumLink.includes('discussions/all');
    const forumLinkPath = window.location.pathname;
    const rootForumId = forumLinkPath.split('/')[5];
    // @ts-expect-error ts-migrate(7005) FIXME: Variable 'rootPath' implicitly has an 'any' type.
    const rootForumLink = path.join(rootPath ?? [], `${courseSlug}/discussions/forums/${rootForumId}`);
    const forumLinkToUse = forumAllThreadsSearchLinkCheck ? rootForumLink : forumLink;
    const postUrl =
      window.location.origin +
      buildUrl(forumLinkToUse, post.questionId, post.topLevelForumAnswerId, post.forumCommentId) +
      window.location.search; // We must include the ?page query param in order to link to the correct page

    return (
      <div
        className="rc-forumsV2_ModerationDropdown"
        css={{ display: 'inline-block' }}
        ref={(node) => {
          this.node = node;
        }}
      >
        <ActionOverflow
          label="More"
          aria-label={ariaLabel}
          size="small"
          data-testid="forums-moderationdropdown"
          aria-describedby={ariaDescribedBy}
        >
          {(postedByUser || canModerate) && (
            <ActionOverflow.Item
              data-js="edit"
              onClick={this.onEditClick}
              data-testid="forums-moderationdropdown-button"
            >
              {_t('Edit')}
            </ActionOverflow.Item>
          )}

          {(postedByUser || canModerate) && (
            <ActionOverflow.Item
              data-js="delete"
              onClick={this.showDeleteModal}
              data-testid="forums-moderationdropdown-button"
            >
              {_t('Delete')}
            </ActionOverflow.Item>
          )}

          {!postedByUser && (
            <ActionOverflow.Item
              data-js="flag"
              onClick={this.onFlaggingClick}
              data-testid="forums-moderationdropdown-button"
            >
              {post.flagDetails && post.flagDetails.isActive ? _t('Resolve report') : _t('Report this')}
            </ActionOverflow.Item>
          )}
          {(hasTeachingRole || isSuperuser) && showingAdminDetails && (
            <ActionOverflow.Item
              data-js="userId"
              onClick={this.hideAdminDetails}
              data-testid="forums-moderationdropdown-button"
            >
              {_t('Hide User ID')}
            </ActionOverflow.Item>
          )}
          {(hasTeachingRole || isSuperuser || isOutsourcing(user)) && !showingAdminDetails && (
            <ActionOverflow.Item
              data-js="userId"
              onClick={this.showAdminDetails}
              data-testid="forums-moderationdropdown-button"
            >
              {_t('Show User ID')}
            </ActionOverflow.Item>
          )}
          {(hasTeachingRole || isSuperuser || isOutsourcing(user)) && (
            <ActionOverflow.Item
              data-js="userId"
              onClick={this.showUserGrades}
              aria-label={_t('View User Grades, opens in a new tab')}
              data-testid="forums-moderationdropdown-button"
            >
              {_t('View User Grades')}
            </ActionOverflow.Item>
          )}
          {isProgrammingItem && hasProgrammingAdminPermissions && (
            <ActionOverflow.Item
              data-js="viewSubmissions"
              onClick={this.showSubmissions}
              data-testid="forums-moderationdropdown-button"
            >
              {_t('View submissions')}
            </ActionOverflow.Item>
          )}

          {!isGradedDiscussionPrompt && (
            <ActionOverflow.Item
              data-js="share"
              onClick={this.onShareClick}
              data-testid="forums-moderationdropdown-button"
            >
              {_t('Link to Post')}
            </ActionOverflow.Item>
          )}
        </ActionOverflow>

        {showDeleteModal && (
          <Delete
            post={post}
            showReasons={!postedByUser}
            showNotifyUser={!isGradedDiscussionPrompt}
            onDeleteSuccess={onDeleteSuccess}
            handleClose={() => this.setState({ showDeleteModal: false })}
          />
        )}

        {showEditThreadModal && (
          <EditThreadModal
            question={post}
            courseId={courseId}
            showReasons={!postedByUser}
            handleClose={() => this.setState({ showEditThreadModal: false })}
          />
        )}

        {showEditWithModerationModal && (
          <EditWithModerationModal
            handleSuccess={this.editWithModerationModalSuccess}
            handleCancel={() => this.setState({ showEditWithModerationModal: false })}
          />
        )}

        {showFlaggingModal && (
          <FlaggingModal post={post} handleClose={() => this.setState({ showFlaggingModal: false })} />
        )}

        {showShareModal && (
          <ShareModal postUrl={postUrl} handleClose={() => this.setState({ showShareModal: false })} />
        )}

        {showHighlightModal && (
          <HighlightModal
            isHighlighted={isHighlighted}
            handleCancel={() => this.setState({ showHighlightModal: false })}
            handleAccept={() => {
              const { executeAction } = this.context;
              if (isHighlighted) {
                executeAction(unhighlightPost, { post });
              } else {
                executeAction(highlightPost, { post });
              }

              this.setState({ showHighlightModal: false });
            }}
          />
        )}
      </div>
    );
  }
}

export default compose<$TSFixMe, $TSFixMe>(
  discussionsForumsHOC({ fields: ['forumType'] }),
  connectToRouter(routerConnectToCurrentForum),
  connectToStores<PropsToComponent, PropsFromCaller>(
    ['CourseMembershipStore', 'CourseStore', 'ApplicationStore', 'ThreadDetailsStore'],
    ({ CourseMembershipStore, CourseStore, ApplicationStore, ThreadDetailsStore }, { currentForum, post }) => {
      const { questionId } = post;
      const itemId = currentForum && currentForum.forumType.definition.itemId;

      const itemMetadata = !!itemId && CourseStore.getMaterials().getItemMetadata(itemId);
      const maybeContentType = itemMetadata && itemMetadata.getTypeName();
      const isProgrammingItem = _.includes(['gradedProgramming', 'ungradedProgramming'], maybeContentType);

      return {
        hasTeachingRole: CourseMembershipStore.hasTeachingRole(),
        isProgrammingItem,
        itemId,
        isSuperuser: ApplicationStore.isSuperuser(),
        authenticated: ApplicationStore.isAuthenticatedUser(),
        question: ThreadDetailsStore.getQuestion(questionId),
      };
    }
  )
)(withTheme(ModerationDropdown));

export const BaseComponent = ModerationDropdown;
